import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scan,
  Shield,
  Banknote,
  ArrowRight,
  X,
  Check,
  Zap,
  FileCheck,
  CreditCard,
  CheckCircle,
  Star,
  Clock,
  Users,
  Phone,
  Camera,
  FileText,
  MessageCircle,
  Truck,
  User,
  Smartphone,
  KeyRound,
  MapPin,
  Car,
  Calendar,
  Search,
  RefreshCw,
  SkipForward,
  Loader2,
  Wrench,
} from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import OtpInput from 'react-otp-input';
import { usePayment } from '@/context/PaymentContext';
import { useCurrency } from '@/context/CurrencyContext';
import {
  fetchInspectionPackages as crmFetchPackages,
  createOrder as crmCreateOrder,
  verifyOrderWithRetry as crmVerifyOrder,
  sendOtp as crmSendOtp,
  verifyOtp as crmVerifyOtp,
  lookupVehicle as crmLookupVehicle,
  toE164India,
  toErrorMessage,
} from '@/services/crmApi';
import { openRazorpay } from '@/services/razorpay';
import DoorstepCarServiceModal from './carservice/DoorstepCarServiceModal';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Razorpay Checkout display config (per CRM handoff v1)
const RZP_NAME = 'Wisedrive';
const RZP_LOGO = 'https://crm.wisedrive.in/static/logo.png';
const RZP_THEME = '#0a7d62';

const Services = ({ onCompareLoan, onBookInspection }) => {
  const { country } = usePayment();
  const { formatPrice, t, selectedCountry } = useCurrency();
  const isIndia = selectedCountry.code === 'IN';
  const navigate = useNavigate();

  const [activeModal, setActiveModal] = useState(null);
  const [doorstepModalOpen, setDoorstepModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [warrantyStep, setWarrantyStep] = useState(1);
  const [claimStep, setClaimStep] = useState('info');
  const [otp, setOtp] = useState('');
  const [warrantyOtp, setWarrantyOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [warrantyOtpSent, setWarrantyOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(30);
  const [warrantyOtpTimer, setWarrantyOtpTimer] = useState(30);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [vehicleData, setVehicleData] = useState(null);
  const [vehicleError, setVehicleError] = useState('');
  const [basePrice, setBasePrice] = useState('1,300');
  // Modern OTP flow: request_id issued by /api/public/otp/send, then submitted
  // to /api/public/otp/verify. Separate state for inspection vs warranty flows.
  const [otpRequestId, setOtpRequestId] = useState(null);
  const [warrantyOtpRequestId, setWarrantyOtpRequestId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    carBrand: '',
    carModel: '',
    preferredDate: '',
    preferredTime: '',
    claimType: '',
    claimDescription: '',
    warrantyId: '',
    registrationNumber: '',
  });
  const { toast } = useToast();
  // OTP Timer for Inspection
  useEffect(() => {
    let interval;
    if (otpSent && otpTimer > 0) {
      interval = setInterval(() => setOtpTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, otpTimer]);

  // OTP Timer for Warranty
  useEffect(() => {
    let interval;
    if (warrantyOtpSent && warrantyOtpTimer > 0) {
      interval = setInterval(() => setWarrantyOtpTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [warrantyOtpSent, warrantyOtpTimer]);

  const [inspectionPackages, setInspectionPackages] = useState([]);
  const handleOpenTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        // Modern CRM API: /api/public/packages?country_code=IN
        // Response shape: { country, packages:[{id,name,description,price,total_check_points,
        //                    no_of_inspections,is_recommended,order,brands_covered,category_details}] }
        const { packages } = await crmFetchPackages(
          selectedCountry?.code || 'IN',
        );
        if (!packages || packages.length === 0) return;

        const mapped = packages.map((pkg) => {
          const nameLower = String(pkg.name || '').toLowerCase();
          const isLuxury =
            nameLower.includes('luxury') || nameLower.includes('premium');
          return {
            id: pkg.id || nameLower || `pkg-${pkg.order ?? 0}`,
            packageInstanceId: pkg.id,
            name:
              nameLower === 'standard'
                ? t('inspectionModal.standard')
                : nameLower === 'luxury'
                  ? t('inspectionModal.luxury')
                  : pkg.name || 'Inspection',
            costPrice: pkg.price || 0,
            taxPercent: 0,
            price: pkg.price || 0,
            subtitle: isLuxury
              ? t('inspectionModal.forPremiumCars')
              : t('inspectionModal.forBudgetCars'),
            brands:
              pkg.brands_covered ||
              (isLuxury
                ? 'Mercedes, BMW, Audi, Volvo, Jaguar, Porsche'
                : 'Maruti, Hyundai, Tata, Honda, Toyota'),
            checkpoints: pkg.total_check_points
              ? `${pkg.total_check_points}+`
              : isLuxury
                ? '240+'
                : '135+',
            recommended: pkg.is_recommended ?? isLuxury,
            features: pkg.category_details,
          };
        });
        const standardPackage =
          mapped.find((pkg) => pkg.name.toLowerCase().includes('standard')) ||
          mapped[0];
        if (standardPackage) setBasePrice(standardPackage.price);
        setInspectionPackages(mapped);
      } catch (err) {
        // Fail-quiet: hero + rest of page still render. Book Inspection button
        // uses the crmApi flow directly, so this only affects the tile grid.
        console.warn(
          '[Services] Unable to fetch inspection packages',
          err?.message || err,
        );
      }
    };
    fetchPackages();
    // packages fetch is keyed on countryCode only; i18n `t` changes don't require refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);
  // Comprehensive inspection categories shown after package selection
  const inspectionCategories = t('inspectionModal.inspectionCategories');
  console.log('selectedCountry', selectedCountry);
  const warrantyPackages = t('warrantyModal.warrantyPackages');

  const inspectionSteps = t('inspectionModal.inspectionSteps');

  const warrantySteps = t('warrantyModal.warrantySteps');

  const handleFormChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSendOtp = async (isWarranty = false) => {
    if (!formData.name || !formData.phone || formData.phone.length !== 10) {
      toast({
        title: t('common.error'),
        description: t('common.errorPhone'),
        variant: 'destructive',
      });
      return;
    }
    try {
      // Modern CRM API: /api/public/otp/send returns { success, request_id, … }.
      // Store request_id so verify step can reference the same session.
      const e164Phone =
        selectedCountry.code === 'MY'
          ? `+60${formData.phone}`
          : toE164India(formData.phone);
      const resp = await crmSendOtp({
        phone: e164Phone,
        country_code: selectedCountry.code || 'IN',
        purpose: isWarranty ? 'warranty_claim' : 'inspection_booking',
      });
      if (isWarranty) {
        setWarrantyOtpRequestId(resp?.request_id || null);
        setWarrantyOtpSent(true);
        setWarrantyOtpTimer(30);
      } else {
        setOtpRequestId(resp?.request_id || null);
        setOtpSent(true);
        setOtpTimer(30);
      }
      toast({
        title: t('common.otpSent'),
        description: t('common.otpSentDesc').replace('{phone}', formData.phone),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: toErrorMessage(error, 'Could not send OTP'),
        variant: 'destructive',
      });
    }
  };

  const handleVerifyOtp = async (isWarranty = false) => {
    const enteredOtp = isWarranty ? warrantyOtp : otp;
    const requestId = isWarranty ? warrantyOtpRequestId : otpRequestId;
    if (enteredOtp.length !== 6) {
      toast({
        title: 'Error',
        description: 'Please enter 6-digit OTP',
        variant: 'destructive',
      });
      return;
    }
    if (!requestId) {
      toast({
        title: t('common.error'),
        description: 'Session expired — please request a new OTP',
        variant: 'destructive',
      });
      return;
    }
    try {
      // Modern CRM API: /api/public/otp/verify returns { success, verification_token, … }.
      const resp = await crmVerifyOtp({
        request_id: requestId,
        otp: enteredOtp,
      });
      if (resp?.success) {
        if (isWarranty) {
          setWarrantyStep(4);
        } else {
          setCurrentStep(4);
        }
        toast({
          title: t('common.verified'),
          description: t('common.verifiedDesc'),
        });
      } else {
        toast({
          title: t('common.error'),
          description: t('common.invalidOtp'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: toErrorMessage(error, t('common.invalidOtp')),
        variant: 'destructive',
      });
    }
  };

  const handleVehicleLookup = async () => {
    if (
      !formData.registrationNumber ||
      formData.registrationNumber.length < 6
    ) {
      toast({
        title: 'Error',
        description: 'Please enter a valid registration number',
        variant: 'destructive',
      });
      return;
    }

    setVehicleLoading(true);
    setVehicleError('');
    setVehicleData(null);

    try {
      // Modern CRM API: /api/public/vehicles/lookup?reg_no=X&country_code=IN
      // Returns { success, vehicle:{reg_no, make, model, variant, fuel_type,
      //          color, registration_date, rto, manufactured_year}, source }.
      const resp = await crmLookupVehicle({
        reg_no: formData.registrationNumber,
        country_code: selectedCountry?.code || 'IN',
      });
      if (resp?.success && resp?.vehicle) {
        const v = resp.vehicle;
        // Preserve legacy `maker_model` field the toast previously used so the
        // UI doesn't need to know which API served the response.
        setVehicleData({
          ...v,
          maker_model: [v.make, v.model].filter(Boolean).join(' ').trim(),
        });
        toast({
          title: 'Vehicle Found!',
          description: [v.make, v.model].filter(Boolean).join(' ').trim(),
        });
      } else {
        throw new Error('vehicle_not_found');
      }
    } catch (error) {
      setVehicleError(
        'Could not fetch vehicle details. Please try again or enter manually.',
      );
      toast({
        title: 'Lookup Failed',
        description: 'Please try again or skip to enter manually',
        variant: 'destructive',
      });
    } finally {
      setVehicleLoading(false);
    }
  };

  const handleConfirmVehicle = () => {
    if (vehicleData) {
      const [maker, ...modelParts] = vehicleData.model.split(' ');
      setFormData({
        ...formData,
        carBrand: maker,
        carModel: modelParts.join(' '),
      });
    }
    setCurrentStep(5);
  };

  const handleSkipVehicleLookup = () => {
    setCurrentStep(5);
  };

  // ---- Razorpay payment (via new CRM /orders/create + Checkout.js) --------
  //
  // We preserve the entire existing pre-payment flow (OTP, vehicle lookup,
  // city + date + time slot) and route through the new CRM contract at the
  // final step. Vehicle + schedule details are forwarded via the CRM notes
  // field so ops has full context when WhatsApp-calling the customer.
  //
  // Package mapping: the legacy getInspectionPlans() returns packages with
  // `packageInstanceId` (old DB). The new CRM /orders/create needs a UUID
  // `package_id`. We resolve by fetching the live CRM package list for the
  // country and matching case-insensitively on `name`.
  const resolveCrmPackageId = async () => {
    if (!selectedPackage) return null;
    const countryCode = (selectedCountry?.code || 'IN').toUpperCase();
    const { packages: crmPkgs } = await crmFetchPackages(countryCode);
    const wanted = (selectedPackage?.name || '').trim().toLowerCase();
    // Prefer exact match, then loose contains, then first recommended fallback.
    const exact = crmPkgs.find((p) => p.name.trim().toLowerCase() === wanted);
    if (exact) return { id: exact.id, crm: exact };
    const loose = crmPkgs.find(
      (p) =>
        p.name.trim().toLowerCase().includes(wanted) ||
        wanted.includes(p.name.trim().toLowerCase()),
    );
    if (loose) return { id: loose.id, crm: loose };
    const rec = crmPkgs.find((p) => p.is_recommended) || crmPkgs[0];
    return rec ? { id: rec.id, crm: rec } : null;
  };

  const buildBookingNotes = () => ({
    source: 'website',
    country: (selectedCountry?.code || 'IN').toUpperCase(),
    // Vehicle (populated by the RTO lookup or manual entry earlier in the flow)
    ...(formData?.registrationNumber
      ? { vehicle_reg_no: formData.registrationNumber }
      : {}),
    ...(vehicleData?.makerDescription || vehicleData?.model
      ? {
          vehicle_make_model: [
            vehicleData?.makerDescription,
            vehicleData?.model,
          ]
            .filter(Boolean)
            .join(' '),
        }
      : {}),
    ...(vehicleData?.fuel ? { vehicle_fuel: vehicleData.fuel } : {}),
    // Schedule (from step 5)
    ...(formData?.preferredDate ? { schedule_date: formData.preferredDate } : {}),
    ...(formData?.preferredTime ? { schedule_slot: formData.preferredTime } : {}),
  });

  const runCrmRazorpay = async ({ partial } = {}) => {
    if (!selectedPackage) return;
    try {
      const resolved = await resolveCrmPackageId();
      if (!resolved) {
        toast({
          title: 'Package unavailable',
          description: 'Please try again or pick a different plan.',
          variant: 'destructive',
        });
        return;
      }

      const phoneE164 =
        selectedCountry?.code === 'MY'
          ? `+60${(formData?.phone || '').replace(/\D/g, '')}`
          : toE164India(formData?.phone || '');

      const payload = {
        package_id: resolved.id,
        country_code: (selectedCountry?.code || 'IN').toUpperCase(),
        customer: {
          name: (formData?.name || '').trim(),
          phone: phoneE164,
          ...(formData?.email?.trim() ? { email: formData.email.trim() } : {}),
        },
        ...(formData?.location ? { city: formData.location } : {}),
        notes: {
          ...buildBookingNotes(),
          ...(partial ? { payment_intent: 'partial_500' } : {}),
        },
      };

      const orderResp = await crmCreateOrder(payload);

      const rzp = await openRazorpay({
        key: orderResp.rzp_key_id,
        amount: orderResp.amount,
        currency: orderResp.currency || 'INR',
        name: RZP_NAME,
        description: `${resolved.crm.name.trim()} · Inspection`,
        image: RZP_LOGO,
        order_id: orderResp.rzp_order_id,
        prefill: {
          name: orderResp.customer?.name || formData?.name || '',
          email: orderResp.customer?.email || formData?.email || '',
          contact: orderResp.customer?.phone || phoneE164,
        },
        notes: { order_id: orderResp.order_id },
        theme: { color: RZP_THEME },
        handler: async (rzpResponse) => {
          try {
            await crmVerifyOrder({
              order_id: orderResp.order_id,
              razorpay_order_id: rzpResponse.razorpay_order_id,
              razorpay_payment_id: rzpResponse.razorpay_payment_id,
              razorpay_signature: rzpResponse.razorpay_signature,
            });
            setCurrentStep(7);
            toast({
              title: 'Booking Confirmed!',
              description:
                'Payment received. Our team will call you on WhatsApp within 30 minutes to schedule your inspection.',
            });
          } catch (e) {
            // Webhook is the safety net — we reassure, not alarm.
            setCurrentStep(7);
            toast({
              title: 'Payment received',
              description:
                'Our team will call you on WhatsApp within 30 minutes to confirm your booking.',
            });
          }
        },
        modal: {
          ondismiss: () => {
            toast({
              title: 'Payment cancelled',
              description: "You can try again whenever you're ready.",
            });
          },
        },
      });

      rzp.on('payment.failed', (resp) => {
        toast({
          title: 'Payment failed',
          description:
            resp?.error?.description ||
            'Your payment did not go through. Please try a different card or UPI.',
          variant: 'destructive',
        });
      });

      rzp.open();
    } catch (e) {
      toast({
        title: "Couldn't start payment",
        description: toErrorMessage(e, 'Please try again in a moment.'),
        variant: 'destructive',
      });
    }
  };

  const handlePayment = async () => {
    await runCrmRazorpay({ partial: false });
  };
  const handleAdvancedPayment = async () => {
    await runCrmRazorpay({ partial: true });
  };
  const handleClaimSubmit = () => {
    setClaimStep('submitted');
    toast({
      title: 'Claim Submitted!',
      description: "We'll contact you within 24 hours.",
    });
  };
  const resetModal = () => {
    setActiveModal(null);
    setSelectedPackage(null);
    setCurrentStep(1);
    setWarrantyStep(1);
    setClaimStep('info');
    setOtp('');
    setWarrantyOtp('');
    setOtpSent(false);
    setWarrantyOtpSent(false);
    setOtpTimer(30);
    setWarrantyOtpTimer(30);
    setVehicleData(null);
    setVehicleError('');
    setFormData({
      name: '',
      phone: '',
      email: '',
      location: '',
      carBrand: '',
      carModel: '',
      preferredDate: '',
      preferredTime: '',
      claimType: '',
      claimDescription: '',
      warrantyId: '',
      registrationNumber: '',
    });
  };

  // Popular cities for selection
  const popularCities =
    selectedCountry.code === 'MY'
      ? [
        { name: t('cities.kualaLumpur'), icon: '🏙️' },
        { name: t('cities.penang'), icon: '🏝️' },
        { name: t('cities.johorBahru'), icon: '🏢' },
        { name: t('cities.shahAlam'), icon: '🕌' },
        { name: t('cities.melaka'), icon: '🏛️' },
        { name: t('cities.ipoh'), icon: '⛰️' },
        { name: t('cities.kotaKinabalu'), icon: '🌊' },
        { name: t('cities.kuching'), icon: '🐆' },
      ]
      : [
        { name: t('cities.bangalore'), icon: '🏢' },
        { name: t('cities.mumbai'), icon: '🌊' },
        { name: t('cities.delhi'), icon: '🏛️' },
        { name: t('cities.hyderabad'), icon: '🕌' },
        { name: t('cities.chennai'), icon: '🏖️' },
        { name: t('cities.pune'), icon: '⛰️' },
        { name: t('cities.kolkata'), icon: '🌉' },
        { name: t('cities.ahmedabad'), icon: '🏙️' },
      ];

  // Time slots for selection
  const timeSlots = [
    { id: 'morning1', label: '9:00 AM', period: 'Morning' },
    { id: 'morning2', label: '10:00 AM', period: 'Morning' },
    { id: 'morning3', label: '11:00 AM', period: 'Morning' },
    { id: 'afternoon1', label: '12:00 PM', period: 'Afternoon' },
    { id: 'afternoon2', label: '2:00 PM', period: 'Afternoon' },
    { id: 'afternoon3', label: '3:00 PM', period: 'Afternoon' },
    { id: 'evening1', label: '4:00 PM', period: 'Evening' },
    { id: 'evening2', label: '5:00 PM', period: 'Evening' },
  ];

  // Generate next 7 days for date selection
  const getNextDays = () => {
    const days = [];
    const today = new Date();
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push({
        date: date.toISOString().split('T')[0],
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString('en-US', { month: 'short' }),
      });
    }
    return days;
  };

  // Stepper Component
  const Stepper = ({ steps, current }) => (
    <div className="flex items-center justify-between mb-6 px-2">
      {steps.map((step, index) => (
        <React.Fragment key={step.num}>
          <div className="flex flex-col items-center">
            <div
              className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${current > step.num
                  ? 'bg-green-500 text-white'
                  : current === step.num
                    ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                    : 'bg-slate-200 text-slate-500'
                }`}
            >
              {current > step.num ? <Check className="h-4 w-4" /> : step.num}
            </div>
            <span
              className={`text-[10px] mt-1 font-medium text-center hidden sm:block ${current >= step.num ? 'text-slate-900' : 'text-slate-400'}`}
            >
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-1 rounded ${current > step.num ? 'bg-green-500' : 'bg-slate-200'}`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderOTPModal = ({ isWarranty = false }) => {
    const currentOtp = isWarranty ? warrantyOtp : otp;
    const timer = isWarranty ? warrantyOtpTimer : otpTimer;
    const prefix = isWarranty ? 'warranty-' : '';

    return (
      <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-5 sm:p-8 w-full max-w-md shadow-2xl mx-2">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="h-16 w-16 sm:h-20 sm:w-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
              <KeyRound className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-slate-900">
              {t('inspectionModal.verifyOtpTitle')}
            </h3>
            <p className="text-slate-500 text-sm sm:text-base mt-2">
              {t('inspectionModal.enterOtp')}
              <br />
              <span className="font-semibold text-slate-700">
                {selectedCountry?.code == 'MY' ? '60' : '91'} {formData.phone}
              </span>
            </p>
          </div>

          {/* OTP Input Boxes - 6 digits */}
          <div className="flex justify-center gap-2 sm:gap-3 mb-6">
            <OtpInput
              className="otp-input"
              value={currentOtp}
              onChange={isWarranty ? setWarrantyOtp : setOtp}
              numInputs={6}
              renderInput={(props) => (
                <input
                  {...props}
                  className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 border-slate-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all bg-slate-50 focus:bg-white focus:outline-none !w-[10px] sm:!w-[50px]"
                  style={{ width: '100%' }}
                />
              )}
              containerStyle={{ gap: '10px' }}
            />
          </div>

          {/* Timer / Resend */}
          <div className="text-center mb-5">
            {timer > 0 ? (
              <p className="text-slate-500 text-sm">
                {t('inspectionModal.resendIn')}{' '}
                <span className="font-bold text-blue-600">{timer}s</span>
              </p>
            ) : (
              <button
                onClick={() => {
                  isWarranty ? setWarrantyOtpTimer(30) : setOtpTimer(30);
                  toast({
                    title: 'OTP Resent!',
                    description: 'Check your phone for the new code.',
                  });
                }}
                className="text-blue-600 font-semibold text-sm hover:underline flex items-center justify-center gap-2 mx-auto"
              >
                <RefreshCw className="h-4 w-4" />{' '}
                {t('inspectionModal.resendOtp')}
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => {
                isWarranty ? setWarrantyOtpSent(false) : setOtpSent(false);
                isWarranty ? setWarrantyOtp('') : setOtp('');
              }}
              className="flex-1 py-3.5 rounded-xl text-sm font-semibold order-2 sm:order-1"
            >
              {t('inspectionModal.changeNumber')}
            </Button>
            <Button
              onClick={() => handleVerifyOtp(isWarranty)}
              disabled={currentOtp.length !== 6}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl font-bold text-sm shadow-lg shadow-blue-600/30 order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`${prefix}verify-otp-btn`}
            >
              {t('inspectionModal.verifyOtpBtn')}
            </Button>
          </div>

          {/* Help Text */}
          <p className="text-center text-xs text-slate-400 mt-4">
            {t('inspectionModal.didntReceive')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <section
        id="services"
        className="py-16 bg-white relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-50 rounded-full blur-3xl opacity-60 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-50 rounded-full blur-3xl opacity-60 translate-y-1/2 -translate-x-1/2" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
              <Star className="h-4 w-4 text-amber-400" />
              {t('services.badge')}
            </div>
            <h2
              className="text-3xl md:text-5xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              {t('services.headline1')}
              <span className="block text-blue-600">
                {t('services.headline2')}
              </span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              {t('services.description')}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Inspection Card */}
            <div className="group relative bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-6 text-white overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="h-14 w-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Scan className="h-7 w-7" />
                  </div>
                  <div className="text-right">
                    <div className="text-blue-200 text-sm">
                      {t('services.inspection.startingFrom')}
                    </div>
                    <div className="text-3xl font-black">
                      {isIndia ? '₹' : 'RM'} {basePrice}
                    </div>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-2">
                  {t('services.inspection.title')}
                </h3>
                <p className="text-blue-100 mb-6">
                  {t('services.inspection.description')}
                </p>
                <div className="space-y-3 mb-6">
                  {t('services.inspection.features').map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="h-5 w-5 bg-white/20 rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3" />
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    if (onBookInspection) onBookInspection();
                    else setActiveModal('inspection');
                  }}
                  data-testid="service-inspection-btn"
                  className="w-full bg-white text-blue-600 py-4 rounded-2xl font-bold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                >
                  {t('services.inspection.bookBtn')}{' '}
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Warranty Card */}
            <div className="group relative bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-6 text-white overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="absolute -top-4 -right-4 bg-amber-400 text-slate-900 px-4 py-1 rounded-full text-xs font-bold rotate-12">
                {t('services.warranty.mostPopular')}
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="h-14 w-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Shield className="h-7 w-7" />
                  </div>
                  <div className="text-right">
                    <div className="text-emerald-200 text-sm">
                      {t('services.inspection.startingFrom')}
                    </div>
                    <div className="text-3xl font-black">
                      {isIndia ? '₹ 1,499' : 'RM 800'}
                    </div>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-2">
                  {t('services.warranty.title')}
                </h3>
                <p className="text-emerald-100 mb-6">
                  {t('services.warranty.description')}
                </p>
                <div className="space-y-3 mb-6">
                  {t('services.warranty.features').map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="h-5 w-5 bg-white/20 rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3" />
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveModal('warranty')}
                    data-testid="service-warranty-btn"
                    className="flex-1 bg-white text-emerald-600 py-4 rounded-2xl font-bold hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {t('services.warranty.buyBtn')}{' '}
                    <ArrowRight className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setActiveModal('claim')}
                    data-testid="service-claim-btn"
                    className="px-4 bg-white/20 backdrop-blur-sm rounded-2xl font-bold hover:bg-white/30 transition-colors"
                    title={t('services.warranty.fileClaim')}
                  >
                    <FileText className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Car Service Card — routes to the dedicated /carservice landing */}
            <div className="group relative bg-gradient-to-br from-slate-900 to-blue-700 rounded-3xl p-6 text-white overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
              <div className="absolute -top-3 -right-3 bg-amber-400 text-slate-900 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-[0.12em] shadow-lg">
                NEW
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="h-14 w-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Wrench className="h-7 w-7" />
                  </div>
                  <div className="text-right">
                    <div className="text-blue-200 text-sm">
                      {t('services.carservice.startingFrom')}
                    </div>
                    <div className="text-3xl font-black">
                      {isIndia ? '₹ 3,999' : 'RM 199'}
                    </div>
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-2">
                  {t('services.carservice.title')}
                </h3>
                <p className="text-blue-100 mb-6">
                  {t('services.carservice.description')}
                </p>
                <div className="space-y-3 mb-6">
                  {t('services.carservice.features').map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="h-5 w-5 bg-white/20 rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3" />
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
                <div className="flex items-stretch gap-2">
                  <button
                    onClick={() => setDoorstepModalOpen(true)}
                    data-testid="service-carservice-btn"
                    className="flex-1 bg-white text-blue-700 py-4 rounded-2xl font-bold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {t('services.carservice.bookBtn')}{' '}
                    <ArrowRight className="h-5 w-5" />
                  </button>
                  <a
                    href="https://wa.me/919905129146?text=Hi%2C%20I%20am%20interested%20in%20car%20service.%20Let%20me%20know%20the%20pricing!"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="service-carservice-whatsapp-btn"
                    title={t('services.carservice.whatsappBtn')}
                    aria-label={t('services.carservice.whatsappBtn')}
                    className="px-4 bg-green-600 hover:bg-green-700 text-white rounded-2xl flex items-center justify-center transition-colors"
                  >
                    <MessageCircle className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Stats */}
          <div className="mt-12 bg-slate-50 rounded-2xl p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {t('services.bottomStats').map((stat, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="h-12 w-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <stat.icon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-xl font-bold text-slate-900">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-500">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== INSPECTION MODAL ==================== */}
      {activeModal === 'inspection' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-3xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">
                    {t('inspectionModal.title')}
                  </h2>
                  <p className="text-blue-100 text-sm">
                    {t('inspectionModal.subtitle')}
                  </p>
                </div>
                <button
                  onClick={resetModal}
                  className="h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {currentStep <= 6 && (
                <Stepper steps={inspectionSteps} current={currentStep} />
              )}

              {/* Step 1: Select Plan */}
              {currentStep === 1 && (
                <>
                  <h3 className="text-lg font-bold text-slate-900 mb-4 text-center">
                    {t('inspectionModal.choosePlan')}
                  </h3>

                  {/* Package Selection Cards */}
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {inspectionPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        onClick={() => setSelectedPackage(pkg)}
                        className={`relative cursor-pointer rounded-2xl p-5 border-2 transition-all ${selectedPackage?.id === pkg.id ? 'border-blue-500 bg-blue-50 shadow-lg' : 'border-slate-200 hover:border-blue-300'}`}
                      >
                        {pkg.recommended && (
                          <div className="absolute -top-2.5 right-4 bg-green-500 text-white px-3 py-0.5 rounded-full text-xs font-bold">
                            {t('inspectionModal.recommended')}
                          </div>
                        )}
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="text-lg font-bold text-slate-900">
                              {pkg.name}
                            </h4>
                            <p className="text-sm text-blue-600">
                              {pkg.subtitle}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-black text-blue-600">
                              {selectedCountry.symbol}
                              {pkg.price.toLocaleString()}
                            </div>
                            <div className="text-xs text-slate-500">
                              {t('inspectionModal.inclTaxes')}
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mb-2">
                          {t('inspectionModal.brands')}: {pkg.brands}
                        </p>
                        <div className="flex items-center gap-2">
                          <div
                            className={`px-3 py-1 rounded-full text-xs font-bold ${selectedPackage?.id === pkg.id ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700'}`}
                          >
                            {pkg.checkpoints} {t('inspectionModal.checkPoints')}
                          </div>
                          {pkg.id === 'luxury' && (
                            <span className="text-xs text-emerald-600 font-semibold">
                              {t('inspectionModal.luxuryAddons')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Comprehensive Inspection Categories - Always Visible */}
                  <div className="bg-slate-50 rounded-2xl p-4 mb-6">
                    <h4 className="text-center font-bold text-slate-900 mb-4 flex items-center justify-center gap-2">
                      <span className="text-blue-600">200+</span>{' '}
                      {t('inspectionModal.checkPoints')}{' '}
                      {t('inspectionModal.inspectionCoverage')}
                    </h4>

                    <div className="grid md:grid-cols-2 gap-4">
                      {t('inspectionModal.inspectionCategories').map(
                        (category) => {
                          const isIncluded = true; //selectedPackage ? category.includedIn.includes(selectedPackage.id) : true;
                          const isLuxuryOnly =
                            !category.includedIn.includes('standard');

                          return (
                            <div
                              key={category.id}
                              className={`bg-white rounded-xl p-4 border transition-all ${isIncluded
                                  ? 'border-blue-200 shadow-sm'
                                  : 'border-slate-200 opacity-50'
                                }`}
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`h-10 w-10 rounded-xl flex items-center justify-center ${isIncluded
                                        ? 'bg-blue-100'
                                        : 'bg-slate-100'
                                      }`}
                                  >
                                    {category.icon === 'car' && (
                                      <Car
                                        className={`h-5 w-5 ${isIncluded ? 'text-blue-600' : 'text-slate-400'}`}
                                      />
                                    )}
                                    {category.icon === 'scan' && (
                                      <Scan
                                        className={`h-5 w-5 ${isIncluded ? 'text-blue-600' : 'text-slate-400'}`}
                                      />
                                    )}
                                    {category.icon === 'shield' && (
                                      <Shield
                                        className={`h-5 w-5 ${isIncluded ? 'text-blue-600' : 'text-slate-400'}`}
                                      />
                                    )}
                                    {category.icon === 'headphones' && (
                                      <Phone
                                        className={`h-5 w-5 ${isIncluded ? 'text-blue-600' : 'text-slate-400'}`}
                                      />
                                    )}
                                  </div>
                                  <div>
                                    <h5
                                      className={`font-semibold text-sm ${isIncluded ? 'text-slate-900' : 'text-slate-500'}`}
                                    >
                                      {category.title}
                                    </h5>
                                    <span
                                      className={`text-xs ${isIncluded ? 'text-blue-600 font-semibold' : 'text-slate-400'}`}
                                    >
                                      {typeof category.points === 'number'
                                        ? `${category.points} Check Points`
                                        : category.points}
                                    </span>
                                  </div>
                                </div>
                                {/* {isLuxuryOnly && (
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isIncluded ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                                  }`}>
                                  LUXURY
                                </span>
                              )} */}
                              </div>

                              {/* Main Items */}
                              <div className="space-y-1.5 mb-3">
                                {category.items.map((item, i) => (
                                  <div
                                    key={i}
                                    className={`flex items-center gap-2 text-xs ${isIncluded ? 'text-slate-700' : 'text-slate-400'}`}
                                  >
                                    <Check
                                      className={`h-3 w-3 flex-shrink-0 ${isIncluded ? 'text-green-500' : 'text-slate-300'}`}
                                    />
                                    {item}
                                  </div>
                                ))}
                              </div>

                              {/* Additional Benefits */}
                              {category.benefits.length > 0 && (
                                <div
                                  className={`border-t pt-2 ${isIncluded ? 'border-slate-100' : 'border-slate-50'}`}
                                >
                                  <p
                                    className={`text-[10px] font-semibold mb-1.5 ${isIncluded ? 'text-slate-500' : 'text-slate-400'}`}
                                  >
                                    {t('inspectionModal.additionalBenefits')}
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {category.benefits
                                      .slice(0, 4)
                                      .map((benefit, i) => (
                                        <span
                                          key={i}
                                          className={`text-[10px] px-2 py-0.5 rounded-full ${isIncluded
                                              ? 'bg-blue-50 text-blue-700'
                                              : 'bg-slate-50 text-slate-400'
                                            }`}
                                        >
                                          {benefit}
                                        </span>
                                      ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        },
                      )}
                    </div>

                    {/* Legend */}
                    {selectedPackage?.id === 'standard' && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-center">
                        <p className="text-sm text-amber-800">
                          <span className="font-semibold">
                            {t('inspectionModal.upgradePrompt')}
                          </span>{' '}
                          {t('inspectionModal.upgradePrompt1')}
                          <span className="text-amber-600 font-bold">
                            {' '}
                            {t('inspectionModal.extraCheckpoints')}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => selectedPackage && setCurrentStep(2)}
                    disabled={!selectedPackage}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold"
                  >
                    {t('inspectionModal.continueWith')}{' '}
                    {selectedPackage?.name || t('inspectionModal.selected')}{' '}
                    {t('inspectionModal.package')}{' '}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </>
              )}

              {/* Step 2: Customer Details */}
              {currentStep === 2 && !otpSent && (
                <>
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-3">
                      <Zap className="h-4 w-4" />{' '}
                      {t('inspectionModal.unlocking')} {selectedPackage?.name}{' '}
                      {t('inspectionModal.inspection')}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {' '}
                      {t('inspectionModal.registerMobile')}
                    </h3>
                    <p className="text-slate-500 text-sm">
                      {' '}
                      {t('inspectionModal.sendOtpDesc')}
                    </p>
                  </div>
                  <div className="max-w-md mx-auto space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('inspectionModal.fullName')} *
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <input
                          name="name"
                          placeholder={t('inspectionModal.enterFullName')}
                          value={formData.name}
                          onChange={handleFormChange}
                          className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('inspectionModal.mobileNumber')} *
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-1 text-slate-500">
                          <Smartphone className="h-5 w-5" />
                          <span className="text-sm font-medium">
                            {selectedCountry?.code == 'MY' ? '+60' : '+91'}
                          </span>
                        </div>
                        <input
                          name="phone"
                          placeholder={t('inspectionModal.digitMobile')}
                          value={formData.phone}
                          onChange={handleFormChange}
                          maxLength={10}
                          className="w-full pl-24 pr-4 py-4 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6 max-w-md mx-auto">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 py-4 rounded-xl"
                    >
                      {t('inspectionModal.back')}
                    </Button>
                    <Button
                      onClick={() => handleSendOtp(false)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold"
                    >
                      {t('inspectionModal.sendOtp')}{' '}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </>
              )}

              {/* OTP Modal for Inspection */}
              {currentStep === 2 &&
                otpSent &&
                renderOTPModal({ isWarranty: false })}

              {/* Step 4: Vehicle Info (After OTP) */}
              {currentStep === 4 && (
                <>
                  <div className="text-center mb-6">
                    <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-2" />
                    <h3 className="text-lg font-bold text-slate-900">
                      {t('inspectionModal.mobileVerified')}
                    </h3>
                    <p className="text-slate-500 text-sm">
                      {t('inspectionModal.enterRegNumber')}
                    </p>
                  </div>

                  <div className="max-w-md mx-auto">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('inspectionModal.regNumber')}
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Car className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                          <input
                            name="registrationNumber"
                            placeholder={t('inspectionModal.regPlaceholder')}
                            value={formData.registrationNumber}
                            onChange={handleFormChange}
                            className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg uppercase"
                          />
                        </div>
                        <Button
                          onClick={handleVehicleLookup}
                          disabled={vehicleLoading}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6 rounded-xl"
                        >
                          {vehicleLoading ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <Search className="h-5 w-5" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {vehicleError && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                        <p className="text-red-600 text-sm">{vehicleError}</p>
                      </div>
                    )}

                    {vehicleData && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="font-semibold text-green-800">
                            {t('inspectionModal.vehicleFound')}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-slate-500">
                              {t('inspectionModal.makeModel')}
                            </span>
                            <div className="font-semibold text-slate-900">
                              {vehicleData.model}
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-500">
                              {t('inspectionModal.fuelType')}
                            </span>
                            <div className="font-semibold text-slate-900">
                              {vehicleData.type}
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-500">
                              {t('inspectionModal.color')}
                            </span>
                            <div className="font-semibold text-slate-900">
                              {vehicleData.vehicleColour}
                            </div>
                          </div>
                          <div>
                            <span className="text-slate-500">
                              {t('inspectionModal.regDate')}
                            </span>
                            <div className="font-semibold text-slate-900">
                              {vehicleData.regDate}
                            </div>
                          </div>
                          <div className="col-span-2">
                            <span className="text-slate-500">
                              {t('inspectionModal.rto')}
                            </span>
                            <div className="font-semibold text-slate-900">
                              {vehicleData.regAuthority}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setVehicleData(null);
                              setFormData({
                                ...formData,
                                registrationNumber: '',
                              });
                            }}
                            className="flex-1 py-2 rounded-lg text-sm"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />{' '}
                            {t('inspectionModal.change')}
                          </Button>
                          <Button
                            onClick={handleConfirmVehicle}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm"
                          >
                            <Check className="h-4 w-4 mr-1" />{' '}
                            {t('inspectionModal.confirm')}
                          </Button>
                        </div>
                      </div>
                    )}

                    {!vehicleData && (
                      <button
                        onClick={handleSkipVehicleLookup}
                        className="w-full flex items-center justify-center gap-2 text-slate-500 hover:text-blue-600 py-3 text-sm font-medium"
                      >
                        <SkipForward className="h-4 w-4" />{' '}
                        {t('inspectionModal.skipEnterManually')}
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* Step 5: Schedule - Modern Design */}
              {currentStep === 5 && (
                <>
                  {/* Package Summary Header */}
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-4 mb-6 flex justify-between items-center text-white">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-white/20 rounded-xl flex items-center justify-center">
                        <Scan className="h-5 w-5" />
                      </div>
                      <div>
                        <span className="font-bold">
                          {selectedPackage?.name} {t('inspectionModal.package')}
                        </span>
                        <p className="text-blue-100 text-xs">
                          {selectedPackage?.checkpoints}{' '}
                          {t('inspectionModal.checkPoints')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-black">
                        {isIndia ? '₹' : 'RM'}
                        {selectedPackage?.price.toLocaleString()}
                      </span>
                      <p className="text-blue-100 text-xs">
                        {t('inspectionModal.inclTaxes')}
                      </p>
                    </div>
                  </div>

                  {/* Car Details - Compact */}
                  {vehicleData ? (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Car className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-green-800">
                            {vehicleData.vehicleManufacturerName +
                              ' ' +
                              vehicleData.model}
                          </p>
                          <p className="text-xs text-green-600">
                            {formData.registrationNumber?.toUpperCase()} -{' '}
                            {vehicleData.type}
                          </p>
                        </div>
                      </div>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 mb-5">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {t('inspectionModal.carBrand')} *
                        </label>
                        <input
                          name="carBrand"
                          placeholder={t('inspectionModal.carBrandPlaceholder')}
                          value={formData.carBrand}
                          onChange={handleFormChange}
                          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {t('inspectionModal.carModel')} *
                        </label>
                        <input
                          name="carModel"
                          placeholder={t('inspectionModal.carModelPlaceholder')}
                          value={formData.carModel}
                          onChange={handleFormChange}
                          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  )}

                  {/* City Selection */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      {t('inspectionModal.selectCity')}
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {popularCities.map((city) => (
                        <button
                          key={city.name}
                          type="button"
                          onClick={() =>
                            setFormData({ ...formData, location: city.name })
                          }
                          className={`p-3 rounded-xl border-2 transition-all text-center ${formData.location === city.name
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                            }`}
                        >
                          <span className="text-xl block mb-1">
                            {city.icon}
                          </span>
                          <span
                            className={`text-xs font-semibold ${formData.location === city.name ? 'text-blue-700' : 'text-slate-700'}`}
                          >
                            {city.name}
                          </span>
                        </button>
                      ))}
                    </div>
                    {/* Other City Input */}
                    <div className="mt-3">
                      <input
                        name="location"
                        placeholder={t('inspectionModal.orTypeCity')}
                        value={
                          !popularCities.some(
                            (c) => c.name === formData.location,
                          )
                            ? formData.location
                            : ''
                        }
                        onChange={(e) =>
                          setFormData({ ...formData, location: e.target.value })
                        }
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>

                  {/* Date Selection */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      {t('inspectionModal.pickDate')}
                    </label>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {getNextDays().map((day) => (
                        <button
                          key={day.date}
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              preferredDate: day.date,
                            })
                          }
                          className={`flex-shrink-0 w-16 p-3 rounded-xl border-2 transition-all text-center ${formData.preferredDate === day.date
                              ? 'border-blue-500 bg-blue-600 text-white shadow-lg'
                              : 'border-slate-200 hover:border-blue-300 bg-white'
                            }`}
                        >
                          <span
                            className={`text-xs block ${formData.preferredDate === day.date ? 'text-blue-100' : 'text-slate-500'}`}
                          >
                            {day.day}
                          </span>
                          <span
                            className={`text-xl font-bold block ${formData.preferredDate === day.date ? 'text-white' : 'text-slate-900'}`}
                          >
                            {day.dayNum}
                          </span>
                          <span
                            className={`text-xs block ${formData.preferredDate === day.date ? 'text-blue-100' : 'text-slate-500'}`}
                          >
                            {day.month}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Time Slot Selection */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-600" />
                      {t('inspectionModal.chooseTime')}
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {timeSlots.map((slot) => (
                        <button
                          key={slot.id}
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              preferredTime: slot.label,
                            })
                          }
                          className={`p-2.5 rounded-xl border-2 transition-all text-center ${formData.preferredTime === slot.label
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-slate-200 hover:border-blue-300'
                            }`}
                        >
                          <span
                            className={`text-sm font-semibold ${formData.preferredTime === slot.label ? 'text-blue-700' : 'text-slate-700'}`}
                          >
                            {slot.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Email - Optional */}
                  <div className="mb-6">
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      {t('inspectionModal.emailOptional')}
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder={t('inspectionModal.emailPlaceholder')}
                      value={formData.email}
                      onChange={handleFormChange}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>

                  {/* Selection Summary */}
                  {(formData.location ||
                    formData.preferredDate ||
                    formData.preferredTime) && (
                      <div className="bg-slate-50 rounded-xl p-4 mb-5">
                        <p className="text-xs text-slate-500 mb-2 font-medium">
                          {t('inspectionModal.yourSelection')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {formData.location && (
                            <span className="inline-flex items-center gap-1 bg-white px-3 py-1.5 rounded-full text-sm border border-slate-200">
                              <MapPin className="h-3 w-3 text-blue-600" />
                              {formData.location}
                            </span>
                          )}
                          {formData.preferredDate && (
                            <span className="inline-flex items-center gap-1 bg-white px-3 py-1.5 rounded-full text-sm border border-slate-200">
                              <Calendar className="h-3 w-3 text-blue-600" />
                              {new Date(
                                formData.preferredDate,
                              ).toLocaleDateString('en-US', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                              })}
                            </span>
                          )}
                          {formData.preferredTime && (
                            <span className="inline-flex items-center gap-1 bg-white px-3 py-1.5 rounded-full text-sm border border-slate-200">
                              <Clock className="h-3 w-3 text-blue-600" />
                              {formData.preferredTime}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(4)}
                      className="flex-1 py-4 rounded-xl"
                    >
                      {t('inspectionModal.back')}
                    </Button>
                    <Button
                      onClick={() => setCurrentStep(6)}
                      disabled={
                        !formData.location ||
                        !formData.preferredDate ||
                        !formData.preferredTime
                      }
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('inspectionModal.proceedPayment')}{' '}
                      <CreditCard className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </>
              )}

              {/* Step 6: Payment - Trustworthy Design */}
              {currentStep === 6 && (
                <div>
                  {/* Order Summary Card */}
                  <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-5 mb-5 text-white">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-lg">
                        {t('inspectionModal.orderSummary')}
                      </h4>
                      <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        {t('inspectionModal.secure')}
                      </span>
                    </div>

                    {/* Package Details */}
                    <div className="bg-white/10 rounded-xl p-4 mb-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-bold text-white">
                            {selectedPackage?.name}{' '}
                            {t('inspectionModal.inspectionLabel')}
                          </p>
                          <p className="text-slate-300 text-sm">
                            {selectedPackage?.checkpoints}{' '}
                            {t('inspectionModal.checkpointInspection')}
                          </p>
                        </div>
                        <p className="text-xl font-bold">
                          {selectedCountry?.symbol}
                          {selectedPackage?.price.toLocaleString()}
                        </p>
                      </div>

                      {/* What's Included */}
                      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/10">
                        {selectedPackage?.id === 'luxury' ? (
                          <>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                              <CheckCircle className="h-3 w-3 text-green-400" />
                              {t('inspectionModal.physicalInsp')}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                              <CheckCircle className="h-3 w-3 text-green-400" />
                              {t('inspectionModal.obd2Test')}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                              <CheckCircle className="h-3 w-3 text-green-400" />
                              {t('inspectionModal.rtoVerification')}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                              <CheckCircle className="h-3 w-3 text-green-400" />
                              {t('inspectionModal.expertAdvice')}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                              <CheckCircle className="h-3 w-3 text-green-400" />
                              {t('inspectionModal.physicalInsp')}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                              <CheckCircle className="h-3 w-3 text-green-400" />
                              {t('inspectionModal.digitalReport')}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                              <CheckCircle className="h-3 w-3 text-green-400" />
                              {t('inspectionModal.fairPriceEst')}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                              <CheckCircle className="h-3 w-3 text-green-400" />
                              {t('inspectionModal.expertAdvice')}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Booking Details */}
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="bg-white/10 px-3 py-1.5 rounded-full flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {formData.location}
                      </span>
                      <span className="bg-white/10 px-3 py-1.5 rounded-full flex items-center gap-1">
                        <Calendar className="h-3 w-3" />{' '}
                        {formData.preferredDate &&
                          new Date(formData.preferredDate).toLocaleDateString(
                            'en-US',
                            {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                            },
                          )}
                      </span>
                      <span className="bg-white/10 px-3 py-1.5 rounded-full flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {formData.preferredTime}
                      </span>
                    </div>
                  </div>

                  {/* Trust Indicators */}
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                      <Shield className="h-6 w-6 text-green-600 mx-auto mb-1" />
                      <p className="text-xs font-semibold text-green-800">
                        {t('inspectionModal.trustIndicators.secure')}
                      </p>
                      <p className="text-[10px] text-green-600">
                        {t('inspectionModal.trustIndicators.encrypted')}
                      </p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                      <RefreshCw className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                      <p className="text-xs font-semibold text-blue-800">
                        {t('inspectionModal.trustIndicators.reschedule')}
                      </p>
                      <p className="text-[10px] text-blue-600">
                        {t('inspectionModal.trustIndicators.upTo24hrs')}
                      </p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                      <Star className="h-6 w-6 text-amber-600 mx-auto mb-1" />
                      <p className="text-xs font-semibold text-amber-800">
                        4.8/5 {t('inspectionModal.trustIndicators.rating')}
                      </p>
                      <p className="text-[10px] text-amber-600">
                        50,000+ {t('inspectionModal.trustIndicators.reviews')}
                      </p>
                    </div>
                  </div>

                  {/* Payment Methods */}
                  {/* <div className="mb-5">
                    <p className="text-sm font-semibold text-slate-700 mb-3">Select Payment Method</p>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { id: 'upi', label: 'UPI', icon: '📱', sublabel: 'GPay, PhonePe, Paytm' },
                        { id: 'card', label: 'Card', icon: '💳', sublabel: 'Credit/Debit' },
                        { id: 'netbanking', label: 'Net Banking', icon: '🏦', sublabel: 'All Banks' }
                      ].map((method) => (
                        <button
                          key={method.id}
                          className="p-4 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-center group"
                        >
                          <span className="text-2xl block mb-1">{method.icon}</span>
                          <span className="text-sm font-semibold text-slate-800 block">{method.label}</span>
                          <span className="text-[10px] text-slate-500">{method.sublabel}</span>
                        </button>
                      ))}
                    </div>
                  </div> */}

                  {/* Total & Pay Button */}
                  <div className="bg-slate-50 rounded-xl p-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-600">
                        {t('inspectionModal.inspectionFee')}
                      </span>
                      <span className="font-semibold">
                        {selectedCountry?.symbol}
                        {selectedPackage?.costPrice.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-600">
                        {t('inspectionModal.taxesIncluded')}
                      </span>
                      <span className="font-semibold text-green-600">
                        {selectedCountry?.symbol}
                        {Math.floor(
                          (selectedPackage?.costPrice *
                            (selectedPackage?.taxPercent || 0)) /
                          100,
                        ).toLocaleString()}
                      </span>
                    </div>
                    <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between items-center">
                      <span className="font-bold text-slate-900">
                        {t('inspectionModal.totalAmount')}
                      </span>
                      <span className="text-2xl font-black text-blue-600">
                        {selectedCountry?.symbol}
                        {selectedPackage?.price.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Money Back Guarantee */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-5 flex items-center gap-3">
                    <div className="h-10 w-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Shield className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">
                        {t('inspectionModal.moneyBack')}
                      </p>
                      <p className="text-xs text-emerald-600">
                        {t('inspectionModal.moneyBackDesc')}
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(5)}
                      className="flex-1 py-4 rounded-xl"
                    >
                      {t('inspectionModal.back')}
                    </Button>
                    <Button
                      onClick={handleAdvancedPayment}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-green-600/30 hover:shadow-xl transition-all"
                    >
                      {t('inspectionModal.pay')} {selectedCountry?.symbol}{' '}
                      {isIndia ? '500' : '50'}
                    </Button>
                    <Button
                      onClick={handlePayment}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-green-600/30 hover:shadow-xl transition-all"
                    >
                      {t('inspectionModal.pay')} {selectedCountry?.symbol}
                      {selectedPackage?.price.toLocaleString()}
                    </Button>
                  </div>

                  {/* Payment Partners */}
                  <div className="mt-4 text-center">
                    <p className="text-[10px] text-slate-400 mb-2">
                      {t('inspectionModal.securedBy')}
                    </p>
                    <div className="flex justify-center gap-4 text-slate-400 text-xs">
                      <span className="px-2 py-1 bg-slate-100 rounded">
                        Razorpay{t('inspectionModal.razorpay')}
                      </span>
                      <span className="px-2 py-1 bg-slate-100 rounded">
                        Visa{t('inspectionModal.visa')}
                      </span>
                      <span className="px-2 py-1 bg-slate-100 rounded">
                        Mastercard{t('inspectionModal.mastercard')}
                      </span>
                      <span className="px-2 py-1 bg-slate-100 rounded">
                        UPI{t('inspectionModal.upi')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 7: Confirmation - Enhanced Design */}
              {currentStep === 7 && (
                <div className="py-2">
                  {/* Success Animation Header */}
                  <div className="text-center mb-6">
                    <div className="relative inline-block">
                      <div className="h-24 w-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-green-500/30">
                        <CheckCircle className="h-12 w-12 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center border-4 border-white">
                        <Zap className="h-4 w-4 text-white" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mt-4 mb-1">
                      {t('inspectionModal.bookingConfirmed')}
                    </h3>
                    <p className="text-slate-500">
                      {t('inspectionModal.bookingId')}:{' '}
                      <span className="font-mono font-semibold text-blue-600">
                        WD{Date.now().toString().slice(-8)}
                      </span>
                    </p>
                  </div>

                  {/* Booking Details Card */}
                  <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-5 mb-5 text-white">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 bg-white/10 rounded-xl flex items-center justify-center">
                          <Car className="h-6 w-6" />
                        </div>
                        <div>
                          <p className="font-bold">
                            {formData.carBrand} {formData.carModel}
                          </p>
                          <p className="text-slate-400 text-sm">
                            {selectedPackage?.name}{' '}
                            {t('inspectionModal.inspectionLabel')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-400">
                          {selectedCountry?.symbol}
                          {selectedPackage?.price.toLocaleString()}
                        </p>
                        <p className="text-xs text-green-300">
                          {t('inspectionModal.paid')}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 bg-white/5 rounded-xl p-4">
                      <div className="text-center">
                        <MapPin className="h-5 w-5 mx-auto mb-1 text-blue-400" />
                        <p className="text-xs text-slate-400">
                          {t('inspectionModal.locationLabel')}
                        </p>
                        <p className="font-semibold text-sm">
                          {formData.location}
                        </p>
                      </div>
                      <div className="text-center border-x border-white/10">
                        <Calendar className="h-5 w-5 mx-auto mb-1 text-blue-400" />
                        <p className="text-xs text-slate-400">
                          {t('inspectionModal.dateLabel')}
                        </p>
                        <p className="font-semibold text-sm">
                          {formData.preferredDate &&
                            new Date(formData.preferredDate).toLocaleDateString(
                              'en-US',
                              {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                              },
                            )}
                        </p>
                      </div>
                      <div className="text-center">
                        <Clock className="h-5 w-5 mx-auto mb-1 text-blue-400" />
                        <p className="text-xs text-slate-400">
                          {t('inspectionModal.timeLabel')}
                        </p>
                        <p className="font-semibold text-sm">
                          {formData.preferredTime}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* What Happens Next */}
                  <div className="mb-5">
                    <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                      <Zap className="h-4 w-4 text-blue-600" />
                      {t('inspectionModal.whatNext')}
                    </h4>
                    <div className="space-y-3">
                      {[
                        {
                          icon: Phone,
                          title: t('inspectionModal.steps.confirmation.title'),
                          desc: t('inspectionModal.steps.confirmation.desc'),
                          time: t('inspectionModal.steps.confirmation.time'),
                        },
                        {
                          icon: User,
                          title: t('inspectionModal.steps.assignment.title'),
                          desc: t('inspectionModal.steps.assignment.desc'),
                          time: t('inspectionModal.steps.assignment.time'),
                        },
                        {
                          icon: Scan,
                          title: t('inspectionModal.steps.inspection.title'),
                          desc: t('inspectionModal.steps.inspection.desc'),
                          time: t('inspectionModal.steps.inspection.time'),
                        },
                        {
                          icon: FileText,
                          title: t('inspectionModal.steps.report.title'),
                          desc: t('inspectionModal.steps.report.desc'),
                          time: t('inspectionModal.steps.report.time'),
                        },
                      ].map((step, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 bg-slate-50 rounded-xl p-3"
                        >
                          <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <step.icon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900 text-sm">
                              {step.title}
                            </p>
                            <p className="text-xs text-slate-500">
                              {step.desc}
                            </p>
                          </div>
                          <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded-full">
                            {step.time}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Contact & Support */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <MessageCircle className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-amber-800 text-sm">
                            {t('inspectionModal.needReschedule')}
                          </p>
                          <p className="text-xs text-amber-600">
                            {t('inspectionModal.freeChanges')}
                          </p>
                        </div>
                      </div>
                      <a
                        href="tel:+911800XXXXXX"
                        className="bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-600 transition-colors"
                      >
                        {t('inspectionModal.callSupport')}
                      </a>
                    </div>
                  </div>

                  {/* Confirmation sent to */}
                  <div className="text-center text-sm text-slate-500 mb-5">
                    <p>
                      {t('inspectionModal.confirmationSent')}{' '}
                      <span className="font-semibold text-slate-700">
                        {selectedCountry?.code == 'MY' ? '+60' : '+91'}{' '}
                        {formData.phone}
                      </span>
                    </p>
                    {formData.email && (
                      <p>
                        &{' '}
                        <span className="font-semibold text-slate-700">
                          {formData.email}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={resetModal}
                      className="flex-1 py-4 rounded-xl"
                    >
                      <X className="h-4 w-4 mr-2" />{' '}
                      {t('inspectionModal.close')}
                    </Button>
                    <Button
                      onClick={resetModal}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold"
                    >
                      {t('inspectionModal.bookAnother')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== WARRANTY MODAL WITH OTP ==================== */}
      {activeModal === 'warranty' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-4 rounded-t-3xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">
                    {t('warrantyModal.title')}
                  </h2>
                  <p className="text-emerald-100 text-sm">
                    {t('warrantyModal.subtitle')}
                  </p>
                </div>
                <button
                  onClick={resetModal}
                  className="h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* {warrantyStep <= 3 && <Stepper steps={warrantySteps} current={warrantyStep} />} */}

              {/* Step 1: Select Plan */}
              {warrantyStep === 1 && (
                <>
                  <h3 className="text-lg font-bold text-slate-900 mb-4 text-center">
                    {t('warrantyModal.choosePlan')}
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    {warrantyPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        onClick={() => setSelectedPackage(pkg)}
                        className={`relative cursor-pointer rounded-2xl p-5 border-2 transition-all ${selectedPackage?.id === pkg.id ? 'border-emerald-500 bg-emerald-50 shadow-lg' : 'border-slate-200 hover:border-emerald-300'}`}
                      >
                        {pkg.recommended && (
                          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-3 py-0.5 rounded-full text-xs font-bold">
                            {t('warrantyModal.bestValue')}
                          </div>
                        )}
                        <h4 className="text-lg font-bold text-slate-900 mb-1">
                          {pkg.name}
                        </h4>
                        <div className="text-2xl font-black text-emerald-600">
                          {selectedCountry.symbol}
                          {pkg.price.toLocaleString()}
                        </div>
                        <p className="text-xs text-slate-500 mb-3">
                          {pkg.period}
                        </p>
                        <div className="bg-slate-100 rounded-lg p-3 mb-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">
                              {t('warrantyModal.coPay')}:
                            </span>
                            <span className="font-bold">{pkg.coPay}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">
                              {t('warrantyModal.wePay')}:
                            </span>
                            <span className="font-bold text-emerald-600">
                              {pkg.payout}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-1">
                          {pkg.features.slice(0, 4).map((f, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 text-xs text-slate-600"
                            >
                              <Check className="h-3 w-3 text-emerald-500" />
                              {f}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={() => selectedPackage && setWarrantyStep(4)}
                    disabled={!selectedPackage}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-xl font-semibold"
                  >
                    {t('warrantyModal.continue')}{' '}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </>
              )}

              {/* Step 2: Customer Details */}
              {warrantyStep === 2 && !warrantyOtpSent && (
                <>
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-3">
                      <Shield className="h-4 w-4" />
                      {t('warrantyModal.unlockingWarranty')}{' '}
                      {selectedPackage?.name} {t('warrantyModal.warranty')}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {t('warrantyModal.registerMobile')}
                    </h3>
                    <p className="text-slate-500 text-sm">
                      {t('warrantyModal.sendOtpDesc')}
                    </p>
                  </div>
                  <div className="max-w-md mx-auto space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('warrantyModal.fullName')} *
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <input
                          name="name"
                          placeholder={t('warrantyModal.enterFullName')}
                          value={formData.name}
                          onChange={handleFormChange}
                          className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-lg"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {t('warrantyModal.mobileNumber')} *
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-1 text-slate-500">
                          <Smartphone className="h-5 w-5" />
                          <span className="text-sm font-medium">
                            {selectedCountry?.code == 'MY' ? '+60' : '+91'}
                          </span>
                        </div>
                        <input
                          name="phone"
                          placeholder={t('warrantyModal.digitMobile')}
                          value={formData.phone}
                          onChange={handleFormChange}
                          maxLength={10}
                          className="w-full pl-24 pr-4 py-4 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-lg"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6 max-w-md mx-auto">
                    <Button
                      variant="outline"
                      onClick={() => setWarrantyStep(1)}
                      className="flex-1 py-4 rounded-xl"
                    >
                      {t('inspectionModal.back')}
                    </Button>
                    <Button
                      onClick={() => handleSendOtp(true)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-xl font-semibold"
                    >
                      {t('warrantyModal.sendOtp')}{' '}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </>
              )}

              {/* OTP Modal for Warranty */}
              {warrantyStep === 2 &&
                warrantyOtpSent &&
                renderOTPModal({ isWarranty: true })}

              {/* Step 4: Confirmation - Team Will Contact */}
              {warrantyStep === 4 && (
                <div className="text-center py-6">
                  <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    {t('warrantyModal.requestSubmitted')}
                  </h3>

                  <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6 max-w-md mx-auto">
                    <div className="flex items-center justify-center gap-3 mb-4">
                      <Phone className="h-6 w-6 text-emerald-600" />
                      <span className="text-lg font-semibold text-emerald-800">
                        {t('warrantyModal.teamWillContact')}
                      </span>
                    </div>
                    <p className="text-emerald-700 text-sm mb-4">
                      {t('warrantyModal.expertWillCall')
                        .replace('{hours}', t('warrantyModal.twoToFourHours'))
                        .replace('{plan}', selectedPackage?.name)}
                    </p>
                  </div>

                  <div className="flex items-center justify-center gap-4 text-sm text-slate-500 mb-6">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{t('warrantyModal.supportHours')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Phone className="h-4 w-4" />
                      <span>
                        {t('warrantyModal.supportPhone')}:{' '}
                        {t('warrantyModal.supportNumber')}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={resetModal}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl font-semibold"
                  >
                    {t('warrantyModal.done')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== CLAIM MODAL ==================== */}
      {activeModal === 'claim' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-4 rounded-t-3xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">{t('claimModal.title')}</h2>
                  <p className="text-orange-100 text-sm">
                    {t('claimModal.subtitle')}
                  </p>
                </div>
                <button
                  onClick={resetModal}
                  className="h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {claimStep === 'info' && (
                <>
                  <div className="text-center mb-6">
                    <div className="h-14 w-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <FileText className="h-7 w-7 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">
                      {t('claimModal.howItWorks')}
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {[
                      {
                        icon: FileText,
                        title: t('claimModal.steps.submit.title'),
                        desc: t('claimModal.steps.submit.desc'),
                      },
                      {
                        icon: Camera,
                        title: t('claimModal.steps.upload.title'),
                        desc: t('claimModal.steps.upload.desc'),
                      },
                      {
                        icon: MessageCircle,
                        title: t('claimModal.steps.review.title'),
                        desc: t('claimModal.steps.review.desc'),
                      },
                      {
                        icon: Truck,
                        title: t('claimModal.steps.repair.title'),
                        desc: t('claimModal.steps.repair.desc'),
                      },
                    ].map((step, index) => (
                      <div
                        key={index}
                        className="bg-slate-50 rounded-xl p-4 text-center"
                      >
                        <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center mx-auto mb-2 shadow-sm">
                          <step.icon className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="font-semibold text-slate-900 text-sm">
                          {step.title}
                        </div>
                        <div className="text-xs text-slate-500">
                          {step.desc}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={() => setClaimStep('form')}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-xl font-semibold"
                  >
                    {t('claimModal.startProcess')}{' '}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </>
              )}
              {claimStep === 'form' && (
                <>
                  <div className="space-y-4 mb-6">
                    <input
                      name="warrantyId"
                      placeholder={t('claimModal.warrantyId')}
                      value={formData.warrantyId}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-orange-500"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        name="name"
                        placeholder={t('warrantyModal.fullName')}
                        value={formData.name}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-orange-500"
                      />
                      <input
                        name="phone"
                        placeholder={t('warrantyModal.mobileNumber')}
                        value={formData.phone}
                        onChange={handleFormChange}
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <select
                      name="claimType"
                      value={formData.claimType}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">
                        {t('claimModal.selectClaimType')}
                      </option>
                      <option value="engine">
                        {t('claimModal.claimTypes.engine')}
                      </option>
                      <option value="transmission">
                        {t('claimModal.claimTypes.transmission')}
                      </option>
                      <option value="electrical">
                        {t('claimModal.claimTypes.electrical')}
                      </option>
                      <option value="ac">
                        {t('claimModal.claimTypes.ac')}
                      </option>
                      <option value="mechanical">
                        {t('claimModal.claimTypes.mechanical')}
                      </option>
                    </select>
                    <textarea
                      name="claimDescription"
                      placeholder={t('claimModal.describeIssue')}
                      value={formData.claimDescription}
                      onChange={handleFormChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-orange-500 resize-none"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setClaimStep('info')}
                      className="flex-1 py-4 rounded-xl"
                    >
                      {t('warrantyModal.back')}
                    </Button>
                    <Button
                      onClick={handleClaimSubmit}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-xl font-semibold"
                    >
                      {t('claimModal.submitClaim')}
                    </Button>
                  </div>
                </>
              )}
              {claimStep === 'submitted' && (
                <div className="text-center py-6">
                  <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {t('claimModal.claimSubmitted')}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    {t('claimModal.contactWithin24')}
                  </p>
                  <Button
                    onClick={resetModal}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-xl font-semibold"
                  >
                    {t('warrantyModal.done')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Doorstep Car Service — internal modal flow (lookup → lead → price reveal) */}
      <DoorstepCarServiceModal
        open={doorstepModalOpen}
        onClose={() => setDoorstepModalOpen(false)}
      />
    </>
  );
};

export default Services;
